<template>
    <div class="order-saga-trace-panel dlm-admin-theme">
        <div class="box box-primary">
            <div class="box-header with-border">
                <h3 class="box-title">Search</h3>
            </div>
            <div class="box-body">
                <form class="form-inline" @submit.prevent="search">
                    <div class="form-group" style="margin-right: 8px;">
                        <label class="sr-only" for="saga-trace-query">Query</label>
                        <input
                            id="saga-trace-query"
                            v-model="query"
                            type="text"
                            class="form-control"
                            style="min-width: 320px;"
                            :placeholder="'Correlation ID or webhook log ID'"
                            autocomplete="off"
                        >
                    </div>
                    <button type="submit" class="btn btn-primary" :disabled="loading || !query.trim()">
                        {{ loading ? 'Loading…' : 'Trace' }}
                    </button>
                </form>
                <p class="help-block" style="margin-top: 10px;">
                    Numeric values search by webhook log ID; other values search by correlation ID.
                    Shopify order name / GID search is not available yet.
                </p>
            </div>
        </div>

        <div v-if="errorMessage" class="alert alert-danger">
            {{ errorMessage }}
        </div>

        <div v-if="trace" class="box box-default">
            <div class="box-header with-border">
                <h3 class="box-title">Saga trace</h3>
                <div class="box-tools pull-right">
                    <span class="label label-default">{{ sagaTypeLabel }}</span>
                </div>
            </div>
            <div class="box-body">
                <dl class="dl-horizontal saga-trace-summary">
                    <dt>Correlation ID</dt>
                    <dd><code>{{ trace.correlation_id }}</code></dd>
                    <dt v-if="trace.webhook_log_id">Webhook log ID</dt>
                    <dd v-if="trace.webhook_log_id"><code>{{ trace.webhook_log_id }}</code></dd>
                </dl>

                <div v-if="trace.saga_type === 'payment'" class="alert alert-info">
                    Payment saga is not connected yet — no hops to display.
                </div>

                <ol v-else class="saga-trace-timeline list-unstyled">
                    <li
                        v-for="slot in timelineSlots"
                        :key="slot.hop"
                        class="saga-trace-timeline__item"
                        :class="timelineItemClass(slot)"
                    >
                        <div class="saga-trace-timeline__marker" />
                        <div class="saga-trace-timeline__content">
                            <div class="saga-trace-timeline__header">
                                <strong>{{ slot.label }}</strong>
                                <span v-if="slot.step" class="label" :class="statusBadgeClass(slot.step.status)">
                                    {{ slot.step.status }}
                                </span>
                                <span v-else class="label label-default">not reached</span>
                                <span
                                    v-if="slot.step && slot.step.downstream_failure"
                                    class="label label-warning"
                                    style="margin-left: 6px;"
                                >
                                    downstream failure
                                </span>
                            </div>

                            <template v-if="slot.step">
                                <p v-if="slot.step.occurred_at" class="text-muted saga-trace-timeline__meta">
                                    {{ slot.step.occurred_at }}
                                </p>
                                <p v-if="slot.step.error" class="text-danger saga-trace-timeline__meta">
                                    {{ slot.step.error }}
                                </p>
                                <p v-if="slot.step.downstream_failure" class="text-warning saga-trace-timeline__meta">
                                    {{ formatDownstreamFailure(slot.step.downstream_failure) }}
                                </p>
                                <ul v-if="linkEntries(slot.step).length" class="list-inline saga-trace-timeline__links">
                                    <li v-for="link in linkEntries(slot.step)" :key="link.key">
                                        <template v-if="link.href">
                                            <a :href="link.href" target="_blank" rel="noopener noreferrer">{{ link.label }}</a>
                                        </template>
                                        <template v-else>
                                            <span class="text-muted">{{ link.label }}:</span>
                                            <code>{{ link.value }}</code>
                                        </template>
                                    </li>
                                </ul>
                            </template>
                        </div>
                    </li>
                </ol>
            </div>
        </div>
    </div>
</template>

<script>
const ORDER_HOPS = [
    { hop: 0, label: 'Webhook ingress' },
    { hop: 1, label: 'Dropship staging' },
    { hop: 2, label: 'DLM transfer' },
    { hop: 3, label: 'BeeStore sync' },
];

export default {
    name: 'OrderSagaTracePanel',
    props: {
        apiUrl: {
            type: String,
            required: true,
        },
        initialCorrelationId: {
            type: String,
            default: '',
        },
        initialWebhookLogId: {
            type: [String, Number],
            default: '',
        },
    },
    data() {
        return {
            query: '',
            loading: false,
            errorMessage: null,
            trace: null,
        };
    },
    computed: {
        sagaTypeLabel() {
            if (!this.trace) {
                return '';
            }

            const type = this.trace.saga_type || 'unknown';

            if (type === 'order') {
                return 'Order saga';
            }

            if (type === 'payment') {
                return 'Payment saga';
            }

            return 'Unknown saga';
        },
        stepsByHop() {
            const map = {};

            if (!this.trace || !Array.isArray(this.trace.steps)) {
                return map;
            }

            this.trace.steps.forEach((step) => {
                map[step.hop] = step;
            });

            return map;
        },
        timelineSlots() {
            if (!this.trace) {
                return [];
            }

            if (this.trace.saga_type === 'payment') {
                return [];
            }

            return ORDER_HOPS.map((definition) => ({
                ...definition,
                step: this.stepsByHop[definition.hop] || null,
            }));
        },
    },
    mounted() {
        if (this.initialWebhookLogId) {
            this.query = String(this.initialWebhookLogId);
            this.search();
            return;
        }

        if (this.initialCorrelationId) {
            this.query = this.initialCorrelationId;
            this.search();
        }
    },
    methods: {
        resolveQueryParams() {
            const trimmed = this.query.trim();

            if (trimmed === '') {
                return null;
            }

            if (/^\d+$/.test(trimmed)) {
                return { webhook_log_id: trimmed };
            }

            return { correlation_id: trimmed };
        },
        async search() {
            const params = this.resolveQueryParams();

            if (!params) {
                this.errorMessage = 'Enter a correlation ID or webhook log ID.';
                this.trace = null;
                return;
            }

            this.loading = true;
            this.errorMessage = null;
            this.trace = null;

            const url = new URL(this.apiUrl, window.location.origin);
            Object.entries(params).forEach(([key, value]) => {
                url.searchParams.set(key, value);
            });

            try {
                const response = await fetch(url.toString(), {
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                    credentials: 'same-origin',
                });

                if (response.status === 404) {
                    this.errorMessage = 'Saga trace not found.';
                    return;
                }

                if (!response.ok) {
                    const body = await response.json().catch(() => ({}));
                    this.errorMessage = body.message || 'Unable to load saga trace.';
                    return;
                }

                const body = await response.json();
                this.trace = body.data || null;
            } catch (error) {
                this.errorMessage = 'Unable to load saga trace.';
            } finally {
                this.loading = false;
            }
        },
        statusBadgeClass(status) {
            switch (status) {
            case 'completed':
                return 'label-success';
            case 'failed':
                return 'label-danger';
            case 'processing':
                return 'label-info';
            case 'pending':
                return 'label-warning';
            default:
                return 'label-default';
            }
        },
        timelineItemClass(slot) {
            if (!slot.step) {
                return 'saga-trace-timeline__item--empty';
            }

            if (slot.step.status === 'failed') {
                return 'saga-trace-timeline__item--failed';
            }

            if (slot.step.downstream_failure) {
                return 'saga-trace-timeline__item--downstream';
            }

            return 'saga-trace-timeline__item--present';
        },
        formatDownstreamFailure(failure) {
            if (!failure || typeof failure !== 'object') {
                return '';
            }

            const parts = [];
            if (failure.code) {
                parts.push(String(failure.code));
            }
            if (failure.message) {
                parts.push(String(failure.message));
            }

            return parts.join(': ') || JSON.stringify(failure);
        },
        stagedOrderUrl(step) {
            const links = step.links || {};
            const store = links.store;
            const stagedOrderId = links.staged_order_id;

            if (!store || stagedOrderId === undefined || stagedOrderId === null) {
                return null;
            }

            return `/admin/${store}-staged-orders/${stagedOrderId}`;
        },
        linkEntries(step) {
            const links = step.links || {};
            const entries = [];

            if (links.webhook_log_id !== undefined && links.webhook_log_id !== null) {
                entries.push({
                    key: 'webhook_log_id',
                    label: 'Webhook log',
                    value: links.webhook_log_id,
                });
            }

            if (links.staging_sync_task_id !== undefined && links.staging_sync_task_id !== null) {
                entries.push({
                    key: 'staging_sync_task_id',
                    label: 'Staging sync task',
                    value: links.staging_sync_task_id,
                });
            }

            const stagedOrderHref = this.stagedOrderUrl(step);
            if (stagedOrderHref) {
                entries.push({
                    key: 'staged_order_id',
                    label: 'Staged order',
                    href: stagedOrderHref,
                    value: links.staged_order_id,
                });
            } else if (links.staged_order_id !== undefined && links.staged_order_id !== null) {
                entries.push({
                    key: 'staged_order_id',
                    label: 'Staged order',
                    value: links.staged_order_id,
                });
            }

            if (links.num_prenotazione !== undefined && links.num_prenotazione !== null) {
                entries.push({
                    key: 'num_prenotazione',
                    label: 'num_prenotazione',
                    value: links.num_prenotazione,
                });
            }

            if (links.order_entity_id !== undefined && links.order_entity_id !== null) {
                entries.push({
                    key: 'order_entity_id',
                    label: 'order_entity_id',
                    value: links.order_entity_id,
                });
            }

            return entries;
        },
    },
};
</script>

<style scoped>
.saga-trace-timeline {
    margin: 20px 0 0;
    padding: 0;
}

.saga-trace-timeline__item {
    display: flex;
    gap: 16px;
    margin-bottom: 20px;
    position: relative;
}

.saga-trace-timeline__item:not(:last-child)::after {
    background: #d2d6de;
    bottom: -20px;
    content: '';
    left: 11px;
    position: absolute;
    top: 28px;
    width: 2px;
}

.saga-trace-timeline__marker {
    background: #3c8dbc;
    border-radius: 50%;
    flex-shrink: 0;
    height: 24px;
    margin-top: 2px;
    width: 24px;
}

.saga-trace-timeline__item--empty .saga-trace-timeline__marker {
    background: #d2d6de;
}

.saga-trace-timeline__item--failed .saga-trace-timeline__marker {
    background: #dd4b39;
}

.saga-trace-timeline__item--downstream .saga-trace-timeline__marker {
    background: #f39c12;
}

.saga-trace-timeline__content {
    flex: 1;
    min-width: 0;
}

.saga-trace-timeline__header {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.saga-trace-timeline__meta {
    margin: 8px 0 0;
}

.saga-trace-timeline__links {
    margin: 8px 0 0;
    padding: 0;
}
</style>
